import { motion, AnimatePresence } from "framer-motion";
import SidebarMenuNodeV2 from "./SidebarMenuNodeV2";
import SidebarMenuSkeleton from "./SidebarMenuSkeleton";
import { SidebarMenuDTO } from "../../types/sidebar";

interface SidebarModuleMenusProps {
  menus: SidebarMenuDTO[];
  loading?: boolean;
}

/**
 * 📄 SidebarModuleMenus (con animación en secuencia)
 *
 * Los menús aparecen uno tras otro (efecto cascada),
 * con fade + slide-in desde la izquierda.
 */
const SidebarModuleMenus: React.FC<SidebarModuleMenusProps> = ({
  menus,
  loading = false,
}) => {
  // 🔸 Skeletons mientras carga
  if (loading) {
    return (
      <div className="ml-5 mt-2 border-l border-gray-700 pl-3 space-y-2">
        {[...Array(3)].map((_, i) => (
          <SidebarMenuSkeleton key={i} />
        ))}
      </div>
    );
  }

  // 🔹 Sin menús disponibles
  if (!menus.length) {
    return (
      <p className="ml-5 mt-2 text-xs text-gray-500 italic">
        No hay menús disponibles
      </p>
    );
  }

  // 🎬 Variants para animación secuencial
  const listVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08, // ⏱️ intervalo entre cada menú
        delayChildren: 0.1, // pequeña espera antes de iniciar
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -12 },
    show: { opacity: 1, x: 0 },
  };

  // ✅ Render animado
  return (
    <AnimatePresence>
      <motion.div
        key="menu-list"
        variants={listVariants}
        initial="hidden"
        animate="show"
        exit="hidden"
        className="ml-5 mt-2 border-l border-gray-700 pl-3 space-y-1"
      >
        {menus.map((menu) => (
          <motion.div key={menu.id ?? menu.code} variants={itemVariants}>
            <SidebarMenuNodeV2 menu={menu} depth={1} />
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

export default SidebarModuleMenus;
